import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { printClassReport, printBulkResult } from "@/lib/reportCard";
import { Button } from "@/components/ui/button";
import { Loader2, GraduationCap, Users, Target, TrendingUp, FileDown, X, ChevronRight } from "lucide-react";

const barCls = (p) => (p >= 75 ? "from-emerald-500 to-emerald-400" : p >= 40 ? "from-amber-500 to-amber-400" : "from-rose-500 to-rose-400");
const medal = (i) => (i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-slate-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-50 text-slate-400");

export default function ClassReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState("");

  const printBulk = async (kind) => {
    if (!detail) return;
    setBulkBusy(kind);
    try {
      const { data } = await api.get(`/reports/exam-results?track=${encodeURIComponent(detail)}`);
      const ok = printBulkResult(detail, data, kind);
      if (!ok) toast.error("Belge penceresi açılamadı. Pop-up iznini kontrol edin.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/class-comparison");
      setRows(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (track) => {
    setDetail(track);
    setDetailLoading(true);
    setDetailRows([]);
    try {
      const { data } = await api.get(`/reports/class-students?track=${encodeURIComponent(track)}`);
      setDetailRows(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const maxNet = Math.max(1, ...rows.map((r) => r.avg_net));

  return (
    <div className="space-y-5" data-testid="class-report">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><GraduationCap size={13} /> Karşılaştırmalı Rapor</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Sınıf / Alan Bazlı Rapor</h1>
          <p className="text-slate-500 text-sm mt-1">Bir gruba tıklayarak öğrencileri net sırasına göre görün. PDF olarak indirebilirsiniz.</p>
        </div>
        {rows.length > 0 && (
          <Button onClick={() => printClassReport(rows)} className="bg-slate-900 hover:bg-slate-800" data-testid="class-report-pdf">
            <FileDown size={16} /> <span className="ml-1">PDF İndir</span>
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : rows.length === 0 ? (
        <div className="card-ta p-12 text-center text-slate-500">Henüz veri yok — öğrenci ekleyip deneme/çalışma kaydı girildikçe rapor dolar.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="class-report-cards">
            {rows.map((r) => (
              <button key={r.track} onClick={() => openDetail(r.track)} className="card-ta p-5 text-left hover:ring-2 hover:ring-indigo-200 transition-all" data-testid={`class-row-${r.track}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-extrabold text-slate-900 flex items-center gap-1">{r.track} <ChevronRight size={16} className="text-slate-300" /></div>
                  <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Users size={13} /> {r.students} öğrenci</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1"><Target size={12} /> Ort. Net</div>
                    <div className="font-display text-2xl font-extrabold text-indigo-700 mt-0.5">{r.avg_net}</div>
                    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mt-1.5">
                      <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.round((r.avg_net / maxNet) * 100)}%` }} />
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">{r.exam_count} deneme</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1"><TrendingUp size={12} /> Tamamlanma</div>
                    <div className="font-display text-2xl font-extrabold text-slate-900 mt-0.5">%{r.completion}</div>
                    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mt-1.5">
                      <div className={`h-full rounded-full bg-gradient-to-r ${barCls(r.completion)}`} style={{ width: `${r.completion}%` }} />
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">{r.done}/{r.total} görev</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="card-ta overflow-x-auto" data-testid="class-report-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-2.5 px-4 font-semibold">Sınıf / Alan</th>
                  <th className="py-2.5 px-4 font-semibold text-center">Öğrenci</th>
                  <th className="py-2.5 px-4 font-semibold text-center">Ort. Net</th>
                  <th className="py-2.5 px-4 font-semibold text-center">Deneme</th>
                  <th className="py-2.5 px-4 font-semibold text-center">Tamamlanma</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.track} onClick={() => openDetail(r.track)} className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer" data-testid={`class-table-row-${r.track}`}>
                    <td className="py-2.5 px-4 font-semibold text-slate-800">{r.track}</td>
                    <td className="py-2.5 px-4 text-center">{r.students}</td>
                    <td className="py-2.5 px-4 text-center font-bold text-indigo-700">{r.avg_net}</td>
                    <td className="py-2.5 px-4 text-center text-slate-500">{r.exam_count}</td>
                    <td className="py-2.5 px-4 text-center font-semibold">%{r.completion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="class-detail-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDetail(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
            <button onClick={() => setDetail(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="class-detail-close"><X size={20} /></button>
            <div className="text-xs font-semibold text-slate-400">Sınıf Detayı</div>
            <h2 className="font-display text-xl font-extrabold text-slate-900 mt-0.5 mb-3">{detail} — Net Sıralaması</h2>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="class-bulk-docs">
              <Button size="sm" onClick={() => printBulk("TYT")} disabled={!!bulkBusy} variant="outline" className="border-blue-300 text-blue-700 h-8 text-xs" data-testid="bulk-tyt">
                {bulkBusy === "TYT" ? <Loader2 className="animate-spin" size={12} /> : <FileDown size={12} />}<span className="ml-1">Toplu TYT</span>
              </Button>
              <Button size="sm" onClick={() => printBulk("AYT")} disabled={!!bulkBusy} variant="outline" className="border-amber-300 text-amber-700 h-8 text-xs" data-testid="bulk-ayt">
                {bulkBusy === "AYT" ? <Loader2 className="animate-spin" size={12} /> : <FileDown size={12} />}<span className="ml-1">Toplu AYT</span>
              </Button>
              <Button size="sm" onClick={() => printBulk("BRANS")} disabled={!!bulkBusy} variant="outline" className="border-violet-300 text-violet-700 h-8 text-xs" data-testid="bulk-brans">
                {bulkBusy === "BRANS" ? <Loader2 className="animate-spin" size={12} /> : <FileDown size={12} />}<span className="ml-1">Toplu Branş</span>
              </Button>
            </div>
            {detailLoading ? (
              <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></div>
            ) : detailRows.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">Bu grupta öğrenci yok.</div>
            ) : (
              <div className="space-y-2" data-testid="class-detail-list">
                {detailRows.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5" data-testid={`class-detail-student-${s.id}`}>
                    <span className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs font-extrabold ${medal(i)}`}>{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 truncate">{s.name}</div>
                      <div className="text-[11px] text-slate-400">{s.exam_count} deneme · %{s.completion} tamamlanma</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-lg font-extrabold text-indigo-700">{s.avg_net}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">ort. net</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
